import React, { useContext, useEffect, useRef, useState } from 'react'
import { Advert, FixedAdvert } from '@chegg-wt/core/components/Advert'
import ResponsiveAdStrategy from '@chegg-wt/core/components/ResponsiveAdStrategy'
import { GetLVPLayoutFn } from '@chegg-wt/core/types/layout'
import { reloadAds } from '@chegg-wt/core/utils/ads'
import {
  LOW_AD_VARIATION1,
  LOW_AD_VARIATION2,
  LOW_AD_VARIATION3,
} from '@chegg-wt/core/state/optimizely/constants'
import * as Styled from './styled'

// The context for whether lvp R3 is enabled and the ref for the content area
// defined here because we only want to partially expose the context through the helpers below
const LvpContext = React.createContext({
  showLvp: false,
  isLvpR3Active: false,
  isStickyDisabled: false,
  lvpContentRef: null as React.MutableRefObject<any>,
  adConfigVersion: null,
  currentPage: null,
})

export const useLvpContext = () => useContext(LvpContext)

// provider to mount in app to say whether or not lvp R3 is enabled, define lvpContentRef
export const LvpProvider = ({
  children,
  showLvp,
  isLvpR3Active,
  isStickyDisabled,
  adConfigVersion,
  currentPage,
}) => {
  // ref for lvp content area to use with a portal
  const lvpContentRef = useRef(null)
  return (
    <LvpContext.Provider
      value={{
        showLvp,
        adConfigVersion,
        isLvpR3Active,
        isStickyDisabled,
        lvpContentRef,
        currentPage,
      }}
    >
      {children}
    </LvpContext.Provider>
  )
}

// hook to determine if lvp should be shown
export const useLvp = () => {
  const { showLvp } = useContext(LvpContext)
  return showLvp
}

//  a wrapping element that adds margin on the right side for the adbar if lvp is active
export const LvpMarginWrapper = ({ isFullWidth = false, children }) => {
  const { adConfigVersion } = useContext(LvpContext)
  const isLvpActive = useLvp()
  const isHrCenterAlign = adConfigVersion === LOW_AD_VARIATION3
  return isLvpActive ? (
    <Styled.AdMarginWrapper layoutHRCenter={isHrCenterAlign} isFullWidth={isFullWidth}>
      {children}
    </Styled.AdMarginWrapper>
  ) : (
    <>{children}</>
  )
}

// The right hand ad sidebar, large video player area, bottom-anchored ad, and checkbox menu
const LargeVideoPlayerLayout = ({ topContent, children }) => {
  const { showLvp, isLvpR3Active, isStickyDisabled, adConfigVersion } = useContext(
    LvpContext
  )
  const [isR3SideviewFixed, setIsR3SideviewFixed] = useState(false)

  useEffect(() => {
    const scrollHandler = () => {
      if (!isR3SideviewFixed && window.pageYOffset >= 330) {
        return setIsR3SideviewFixed(true)
      }
      if (isR3SideviewFixed && window.pageYOffset < 330) {
        return setIsR3SideviewFixed(false)
      }
    }

    window.addEventListener('scroll', scrollHandler)

    return () => {
      window.removeEventListener('scroll', scrollHandler)
    }
  }, [isR3SideviewFixed])

  // "low ad test EB & CM", Removing lvp ad space, if any of variant is active.
  const hideLvpAdFrame =
    adConfigVersion === LOW_AD_VARIATION1 ||
    adConfigVersion === LOW_AD_VARIATION2 ||
    adConfigVersion === LOW_AD_VARIATION3

  // if lvp is not shown (e.g. the user is paid, or on an auth page), just render top content / children
  if (!showLvp) {
    return (
      <>
        <Styled.Background>
          <Styled.Contents>{topContent()}</Styled.Contents>
        </Styled.Background>
        {children}
        <Styled.BottomTray showLvp={showLvp}>
          <Styled.CheckboxMenu id="checkbox-menu" />
        </Styled.BottomTray>
      </>
    )
  }
  const isHrCenterAlign = adConfigVersion === LOW_AD_VARIATION3

  // sideView is before the video area because they are both using fixed position
  // and the video is supposed to be on top. This fixes the stacking order.
  // More info: https://philipwalton.com/articles/what-no-one-told-you-about-z-index/
  return (
    <>
      <ResponsiveAdStrategy renderAds={reloadAds} />
      {/* the right ad sidebar where the ads and docked video are placed */}
      {isLvpR3Active ? (
        <>
          {isStickyDisabled ? (
            <Styled.SideView data-test="side-view">
              <Advert placement="R1" />
              <Advert placement="R2" />
              <Advert placement="R3" />
            </Styled.SideView>
          ) : (
            <>
              <Styled.SideView data-test="side-view">
                <Advert placement="R1" />
              </Styled.SideView>
              <Styled.StickySideView isR3SideviewFixed={isR3SideviewFixed}>
                <Advert placement="R2" />
                <Advert placement="R3" />
              </Styled.StickySideView>
            </>
          )}
        </>
      ) : (
        <Styled.StickySideView data-test="side-view">
          <Advert placement="R1" />
          <Advert placement="R2" />
        </Styled.StickySideView>
      )}

      {/* the video player & top content area */}
      <Styled.Background>
        <Styled.AdMarginWrapper layoutHRCenter={isHrCenterAlign}>
          <Styled.Contents role="region" aria-label="Page Header">
            <Styled.T1Advert placement="T1" className="adds top" />
            {topContent()}
            {!hideLvpAdFrame && <Styled.VideoAdvert placement="VID" />}
          </Styled.Contents>
        </Styled.AdMarginWrapper>
      </Styled.Background>

      {/* the actual page content */}
      {children}

      {/* the bottom fixed placement ad and checkbox menu portal for bibliography / folders page */}
      <Styled.BottomTray showLvp={showLvp}>
        <FixedAdvert placement="B1" />
        <Styled.CheckboxMenu id="checkbox-menu" />
      </Styled.BottomTray>
    </>
  )
}

export const getLvpLayout: GetLVPLayoutFn = (page, topContent = () => null) => (
  <LargeVideoPlayerLayout topContent={topContent}>{page}</LargeVideoPlayerLayout>
)

export default LargeVideoPlayerLayout
